import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';

const ProductionLinesManagement = () => {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true });

  useEffect(() => {
    fetchLines();
  }, []);

  const fetchLines = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/production-lines/`);
      setLines(response.data.results || response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLine) {
        await axios.patch(`${API_URL}/api/production-lines/${editingLine.id}/`, formData);
      } else {
        await axios.post(`${API_URL}/api/production-lines/`, formData);
      }
      fetchLines();
      setShowForm(false);
      setEditingLine(null);
      setFormData({ name: '', description: '', is_active: true });
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || 'Erreur inconnue'));
    }
  };

  const handleEdit = (line) => {
    setEditingLine(line);
    setFormData({
      name: line.name,
      description: line.description || '',
      is_active: line.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette chaîne ?')) {
      try {
        await axios.delete(`${API_URL}/api/production-lines/${id}/`);
        fetchLines();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner h-12 w-12"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="section-header">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="section-title">Gestion des Chaînes</h2>
            <p className="section-subtitle">Créez et gérez vos chaînes de production</p>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingLine(null);
              setFormData({ name: '', description: '', is_active: true });
            }}
            className="btn-primary flex items-center space-x-2 group"
          >
            <span className="text-xl group-hover:rotate-90 transition-transform duration-300">+</span>
            <span>Ajouter une chaîne</span>
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setEditingLine(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-corporate-900">
                {editingLine ? 'Modifier' : 'Ajouter'} une chaîne
              </h3>
              <button
                onClick={() => { setShowForm(false); setEditingLine(null); }}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-600"
                aria-label="Fermer"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-semibold text-corporate-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-modern"
                  placeholder="Nom de la chaîne"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-corporate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  className="input-modern"
                  placeholder="Description de la chaîne..."
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="text-sm font-semibold text-corporate-700">
                  Chaîne active
                </label>
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
                <button type="submit" className="btn-primary flex-1">
                  {editingLine ? 'Enregistrer' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingLine(null); }}
                  className="btn-secondary flex-1"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {lines.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-5xl mb-4">🏭</div>
          <h3 className="text-xl font-bold text-corporate-900 mb-2">Aucune chaîne</h3>
          <p className="text-gray-600 mb-6">Commencez par créer votre première chaîne</p>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingLine(null);
              setFormData({ name: '', description: '', is_active: true });
            }}
            className="btn-primary"
          >
            + Créer une chaîne
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {lines.map((line, index) => (
            <div
              key={line.id}
              className="card-hover animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="flex items-center space-x-3 sm:space-x-4 mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-primary rounded-lg flex items-center justify-center shadow-corporate flex-shrink-0">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-corporate-900 truncate">
                    {line.name}
                  </h3>
                  {line.is_active ? (
                    <span className="badge-success mt-1">Actif</span>
                  ) : (
                    <span className="badge-danger mt-1">Inactif</span>
                  )}
                </div>
              </div>

              {line.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">{line.description}</p>
              )}

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleEdit(line)}
                  className="btn-primary flex-1 text-sm py-2"
                >
                  Modifier
                </button>
                <button
                  onClick={() => handleDelete(line.id)}
                  className="btn-danger flex-1 text-sm py-2"
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductionLinesManagement;
